import { z } from 'zod'

import { DeliveryCompanySchema } from '~/@entities/Delivery'
import { CoverDataSchema } from '~/@entities/DesignTemplate'
import { WorkbookPartSchema } from '~/@entities/PodCart'
import {
  CartProductTypeSchema,
  PdfTypeSchema,
  PrintTypeSchema,
  ProductStatusSchema,
  ProductTypeSchema,
} from '~/@entities/PodProduct'
import {
  AllGradeSubjectSchema,
  RevisionSchema,
  SchoolTypeSchema,
  SemesterSchema,
} from '~/@entities/SchoolSystem'
import { SignatureTypeSchema } from '~/@entities/Workbook'

/***************** 주문 상태 *****************/
export const ORDER_STATUS = [
  'PRE_PAYMENT',
  'PAYMENT_WAITING',
  'PAYMENT_COMPLETED',
  'IN_PRODUCTION',
  'PRODUCTION_COMPLETED',
  'IN_DELIVERY',
  'DELIVERY_COMPLETED',
  'CANCELED_BY_USER',
  'CANCELED_BY_ADMIN',
] as const

/***************** 결제 대행사 *****************/
// 결제 금액이 0원이면 대행사를 거치지 않는다(ZERO_PAYMENT)
export const PAYMENT_COMPANY = ['TOSS_PAYMENTS', 'ZERO_PAYMENT'] as const

/***************** 쿠폰 혜택 *****************/
export const COUPON_BENEFIT_TYPE = [
  'DELIVERY_DISCOUNT',
  'PRODUCT_DISCOUNT',
  'PRODUCT_FREE',
  'PRODUCT_PERCENT_DISCOUNT',
] as const

/***************** 쿠폰 사용 조건 *****************/
export const COUPON_CONDITION_TYPE = ['MIN_ORDER_ITEM_COUNT', 'MIN_PAYMENT_AMOUNT'] as const

/***************** 쿠폰 사용 상태 *****************/
// 난수 코드로 찾은 쿠폰은 아직 등록하지 않은 상태(UNREGISTERED)로 온다
export const COUPON_USAGE_STATUS = ['UNREGISTERED', 'UNUSED', 'USED'] as const

export const OrderStatusSchema = z.enum(ORDER_STATUS)
export type OrderStatus = z.infer<typeof OrderStatusSchema>

export const PaymentCompanySchema = z.enum(PAYMENT_COMPANY)
export type PaymentCompany = z.infer<typeof PaymentCompanySchema>

export const CouponBenefitTypeSchema = z.enum(COUPON_BENEFIT_TYPE)
export type CouponBenefitType = z.infer<typeof CouponBenefitTypeSchema>

export const CouponConditionTypeSchema = z.enum(COUPON_CONDITION_TYPE)
export type CouponConditionType = z.infer<typeof CouponConditionTypeSchema>

export const CouponUsageStatusSchema = z.enum(COUPON_USAGE_STATUS)
export type CouponUsageStatus = z.infer<typeof CouponUsageStatusSchema>

const _LocalDateTimeSchema = z.iso.datetime({ local: true })
const _LocalDateSchema = z.iso.date()

/***************** 배송지 *****************/
// 주문서·주문·학부모 결제 정보가 같은 배송지 칸을 싣는다. 서버는 같은 값을 name·phoneNumber·address 등의
// 별칭으로 한 번 더 싣는데 받지 않는다.
const _DeliveryAddressSchema = z.object({
  deliveryRecipientName: z.string().nullable(),
  deliveryRecipientPhoneNumber: z.string().nullable(),
  deliveryRecipientEmail: z.string().nullable(),
  deliveryAddress: z.string().nullable(),
  deliveryAddressDetail: z.string().nullable(),
  deliveryZipCode: z.string().nullable(),
  deliveryMemo: z.string().nullable(),
})

/***************** 교재몰 사용자 *****************/
// 교재몰 서버의 사용자 정보. 기본 배송지·포인트·무료 체험 한도를 담는다.
// type 은 선생님(MATHFLAT_TEACHER) 하나뿐이라 받지 않는다.
export const PodUserSchema = _DeliveryAddressSchema.extend({
  id: z.number(),
  academyId: z.string(),
  deliveryRecipientName: z.string(),
  point: z.number(),
  totalOrderQuantity: z.number(),
  freeTrialOrderQuantityLimit: z.number(),
  // 요금제 상시 할인율. 할인 이벤트가 없으면 0
  productDiscountRate: z.number(),
})
export type PodUser = z.infer<typeof PodUserSchema>

/***************** 배송지 주소록 *****************/
// 학원(D코드) 주소록 한 건. 수령인·연락처는 주소록이 쥐지 않는다. id 가 null 이면 주소록 밖의 최근 주문 배송지
// (수정·삭제 불가)이고, label 이 null 이면 이관 항목이다. 위 주문서·주문의 배송지 칸과는 다른 모양이다.
export const DeliveryAddressSchema = z.object({
  id: z.number().nullable(),
  label: z.string().nullable(),
  zipCode: z.string().nullable(),
  address: z.string().nullable(),
  addressDetail: z.string().nullable(),
  // 요청한 선생님의 최근 주문 배송지. 목록 맨 위 최대 1건
  isRecentOrder: z.boolean(),
})
export type DeliveryAddress = z.infer<typeof DeliveryAddressSchema>
export type DeliveryAddressId = NonNullable<DeliveryAddress['id']>

// 로그인한 선생님의 직전 주문 수령인
export const RecentRecipientSchema = z.object({
  name: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  email: z.string().nullable(),
})
export type RecentRecipient = z.infer<typeof RecentRecipientSchema>

// 주소록 조회는 서버 표시 순서 그대로(최근 주문 배송지가 맨 위) 온다. 주문 이력이 없으면 recentRecipient 가 null 이다.
export const DeliveryAddressBookSchema = z.object({
  recentRecipient: RecentRecipientSchema.nullable(),
  addresses: z.array(DeliveryAddressSchema),
})
export type DeliveryAddressBook = z.infer<typeof DeliveryAddressBookSchema>

/***************** 상시 할인 이벤트 *****************/
export const ProductDiscountEventSchema = z.object({
  name: z.string(),
  benefitName: z.string(),
  discountRate: z.number(),
  endDate: _LocalDateSchema,
})
export type ProductDiscountEvent = z.infer<typeof ProductDiscountEventSchema>

// 주문서에 적용했을 때 깎이는 금액이 더해진다
export const PreOrderProductDiscountEventSchema = ProductDiscountEventSchema.extend({
  discountAmount: z.number(),
})
export type PreOrderProductDiscountEvent = z.infer<typeof PreOrderProductDiscountEventSchema>

/***************** 쿠폰 *****************/
// 내 쿠폰 목록은 쿠폰 자체만, 주문서의 쿠폰 목록은 그 주문서에 적용했을 때의 할인액·사용 가능 여부를 더 싣는다.
export const CouponSchema = z.object({
  id: z.number(),
  name: z.string(),
  benefitType: CouponBenefitTypeSchema,
  benefitName: z.string(),
  benefitValue: z.number(),
  conditionType: CouponConditionTypeSchema.nullable(),
  conditionValue: z.number().nullable(),
  conditionName: z.string(),
  endDate: _LocalDateSchema,
  usageStatus: CouponUsageStatusSchema,
  discountAmount: z.number().optional(),
  usable: z.boolean().optional(),
})
export type Coupon = z.infer<typeof CouponSchema>

/***************** 배송 소요일 *****************/
export const DeliveryLeadTimeSchema = z.object({
  minBusinessDays: z.number(),
  maxBusinessDays: z.number(),
})
export type DeliveryLeadTime = z.infer<typeof DeliveryLeadTimeSchema>

/***************** 주문서 항목 *****************/
// 장바구니에서 고른 항목이 주문서로 넘어온 것. 상품 종류마다 다른 클래스를 판별 필드 없이 내보낸다.
// fulltitle 은 name 과 같은 값이라 받지 않는다.
const _PreOrderItemCommonSchema = z.object({
  cartItemId: z.number(),
  name: z.string(),
  productId: z.number(),
  thumbnailImageUrl: z.string(),
  quantity: z.number(),
  price: z.number(),
  productAmount: z.number(),
  discountAmount: z.number(),
  // 추가 금액을 뺀 판매가 × 수량
  totalAmount: z.number(),
  printType: PrintTypeSchema,
  pageCount: z.number(),
  studentNames: z.array(z.string()),
})

export const SignatureWorkbookPreOrderItemSchema = _PreOrderItemCommonSchema.extend({
  productType: z.literal('SIGNATURE_WORKBOOK'),
  customSignatureWorkbookId: z.number(),
  pdfType: PdfTypeSchema,
  coverData: CoverDataSchema,
  showPrice: z.boolean(),
  pairFlag: z.boolean(),
  autoScored: z.boolean(),
})

export const CustomWorkbookPreOrderItemSchema = _PreOrderItemCommonSchema.extend({
  productType: z.literal('CUSTOM_WORKBOOK'),
  customWorkbookId: z.number(),
  pdfType: PdfTypeSchema,
  coverType: z.number(),
  coverData: CoverDataSchema.nullable(),
  innerPdfUrl: z.string(),
  showPrice: z.boolean(),
  pairFlag: z.boolean(),
  autoScored: z.boolean(),
})

export const PrintDocumentPreOrderItemSchema = _PreOrderItemCommonSchema.extend({
  productType: z.literal('PRINT_DOCUMENT'),
  coverData: CoverDataSchema,
})

export const PreOrderItemSchema = z.discriminatedUnion('productType', [
  SignatureWorkbookPreOrderItemSchema,
  CustomWorkbookPreOrderItemSchema,
  PrintDocumentPreOrderItemSchema,
])
export type PreOrderItem = z.infer<typeof PreOrderItemSchema>

/***************** 주문서 *****************/
// 결제 전 주문 초안. 서버가 짧은 시간만 보관한다
export const PreOrderSchema = _DeliveryAddressSchema.extend({
  id: z.string(),
  deliveryRecipientName: z.string(),
  items: z.array(PreOrderItemSchema),
  userCouponId: CouponSchema.shape.id.nullable(),
  couponName: z.string().nullable(),
  productAmount: z.number(),
  deliveryAmount: z.number(),
  // 쿠폰 할인액
  discountAmount: z.number(),
  productDiscountAmount: z.number(),
  pointDiscountAmount: z.number(),
  totalAmount: z.number(),
  paymentAmount: z.number(),
})
export type PreOrder = z.infer<typeof PreOrderSchema>

/***************** 주문 *****************/
const _OrderCommonSchema = _DeliveryAddressSchema.extend({
  id: z.string(),
  status: OrderStatusSchema,
  orderDatetime: _LocalDateTimeSchema,
  paymentDatetime: _LocalDateTimeSchema.nullable(),
  // 학부모 결제 요청의 결제 기한
  paymentExpirationDate: _LocalDateSchema.nullable(),
  orderItemProductTypes: z.array(ProductTypeSchema),
  repProductName: z.string(),
  repProductThumbnailImageUrl: z.string(),
  totalQuantity: z.number(),
  productAmount: z.number(),
  additionalAmount: z.number(),
  deliveryAmount: z.number(),
  // 쿠폰 할인액
  discountAmount: z.number(),
  totalAmount: z.number(),
  userCouponId: CouponSchema.shape.id.nullable(),
  couponName: z.string().nullable(),
  paymentCompany: PaymentCompanySchema.nullable(),
  paymentMethod: z.string().nullable(),
  deliveryCompany: DeliveryCompanySchema.nullable(),
  deliveryTrackingNumber: z.string().nullable(),
  // 학부모 결제 주문에서 학생 이름별로 배송한다
  deliveryRecipientStudentNames: z.array(z.string()).nullable(),
})

// 선생님이 직접 결제한 주문. 서버는 productName·productThumbnailImageUrl 을 rep* 와 같은 값으로 한 번 더 싣는데
// 받지 않는다.
export const DirectOrderSchema = _OrderCommonSchema.extend({
  productDiscountAmount: z.number(),
  productDiscountEventName: z.string().nullable(),
  productDiscountRate: z.number().nullable(),
  pointDiscountAmount: z.number(),
  // 상품 금액 + 추가 금액
  paymentAmount: z.number(),
})
export type DirectOrder = z.infer<typeof DirectOrderSchema>

/***************** 주문 항목 *****************/
// 상품 종류마다 다른 클래스를 판별 필드 없이 내보낸다. KMM 시상품 주문의 항목도 같은 응답으로 오지만
// 앱은 KMM 주문의 항목을 조회하지 않아 받지 않는다.
const _OrderItemCommonSchema = z.object({
  id: z.number(),
  orderId: z.string(),
  name: z.string(),
  productId: z.number(),
  productStatus: ProductStatusSchema,
  thumbnailImageKey: z.string(),
  thumbnailImageUrl: z.string(),
  quantity: z.number(),
  price: z.number(),
  discountedPrice: z.number(),
  additionalPrice: z.number(),
  totalAmount: z.number(),
  printType: PrintTypeSchema,
  studentNames: z.array(z.string()),
  showPrice: z.boolean(),
})

// 교재 정보(학년·교육과정·쪽수)는 주문 당시 시그니처 교재가 지워졌으면 null 이다
export const SignatureWorkbookOrderItemSchema = _OrderItemCommonSchema.extend({
  productType: z.literal('SIGNATURE_WORKBOOK'),
  customSignatureWorkbookId: z.number(),
  pdfType: PdfTypeSchema,
  coverData: CoverDataSchema,
  workbookParts: z.array(WorkbookPartSchema).nullable(),
  problemPageCount: z.number().nullable(),
  problemAnswerPageCount: z.number().nullable(),
  revision: RevisionSchema.nullable(),
  schoolType: SchoolTypeSchema.nullable(),
  grade: AllGradeSubjectSchema.nullable(),
  semester: SemesterSchema,
  signatureType: SignatureTypeSchema.nullable(),
})
export type SignatureWorkbookOrderItem = z.infer<typeof SignatureWorkbookOrderItemSchema>

export const CustomWorkbookOrderItemSchema = _OrderItemCommonSchema.extend({
  productType: z.literal('CUSTOM_WORKBOOK'),
  customWorkbookId: z.number(),
  pdfType: PdfTypeSchema,
  coverNumber: z.number().nullable(),
  coverData: CoverDataSchema.nullable(),
  innerPdfUrl: z.string(),
  problemPageCount: z.number(),
  problemAnswerPageCount: z.number(),
  problemColorPrice: z.number(),
  problemBwPrice: z.number(),
  problemAnswerColorPrice: z.number(),
  problemAnswerBwPrice: z.number(),
})
export type CustomWorkbookOrderItem = z.infer<typeof CustomWorkbookOrderItemSchema>

export const PrintDocumentOrderItemSchema = _OrderItemCommonSchema.extend({
  productType: z.literal('PRINT_DOCUMENT'),
  printDocumentId: z.number(),
  fileKey: z.string(),
  coverData: CoverDataSchema,
  pageCount: z.number(),
})
export type PrintDocumentOrderItem = z.infer<typeof PrintDocumentOrderItemSchema>

export const OrderItemSchema = z.discriminatedUnion('productType', [
  SignatureWorkbookOrderItemSchema,
  CustomWorkbookOrderItemSchema,
  PrintDocumentOrderItemSchema,
])
export type OrderItem = z.infer<typeof OrderItemSchema>

/***************** 학부모 결제 주문 묶음 *****************/
// 한 번의 학부모 결제 요청으로 학생마다 생긴 주문들의 묶음
export const ParentOrderSchema = z.object({
  groupOrderId: z.string(),
  productList: z.array(
    z.object({
      productName: z.string(),
      productThumbnailImageUrl: z.string(),
      orderItemProductTypes: z.array(CartProductTypeSchema),
      orderDatetime: _LocalDateTimeSchema,
      totalQuantity: z.number(),
      price: z.number(),
      discountedPrice: z.number(),
      additionalPrice: z.number(),
    }),
  ),
})
export type ParentOrder = z.infer<typeof ParentOrderSchema>

export const ParentOrderDetailSchema = z.object({
  groupOrderId: ParentOrderSchema.shape.groupOrderId,
  groupOrderItems: z.array(OrderItemSchema),
  orders: z.array(
    _OrderCommonSchema.extend({
      orderItems: z.array(
        z.object({
          productName: z.string(),
          productStatus: ProductStatusSchema,
          price: z.number(),
          discountedPrice: z.number(),
          additionalPrice: z.number(),
          showPrice: z.boolean(),
        }),
      ),
    }),
  ),
})
export type ParentOrderDetail = z.infer<typeof ParentOrderDetailSchema>

/***************** 학부모 결제 정보 *****************/
// 학부모가 결제 링크로 들어와 보는 주문 한 건
export const ParentPaymentSchema = _DeliveryAddressSchema.extend({
  id: z.string(),
  status: OrderStatusSchema,
  orderDatetime: _LocalDateTimeSchema,
  orderItemProductTypes: z.array(ProductTypeSchema),
  orderItemList: z.array(
    z.object({
      name: z.string(),
      thumbnailImageUrl: z.string(),
      salesPrice: z.number(),
      quantity: z.number(),
      totalAmount: z.number(),
    }),
  ),
  totalQuantity: z.number(),
  // 상품 금액 + 추가 금액
  productAmount: z.number(),
  deliveryAmount: z.number(),
  discountAmount: z.number(),
  totalAmount: z.number(),
  paymentAmount: z.number(),
  userCouponId: CouponSchema.shape.id.nullable(),
  couponName: z.string().nullable(),
  paymentCompany: PaymentCompanySchema.nullable(),
  paymentMethod: z.string().nullable(),
  deliveryCompany: DeliveryCompanySchema.nullable(),
  deliveryTrackingNumber: z.string().nullable(),
  deliveryRecipientStudentNames: z.array(z.string()).nullable(),
  deliveryLeadTime: DeliveryLeadTimeSchema,
})
export type ParentPayment = z.infer<typeof ParentPaymentSchema>

/***************** KMM 시상품 주문 *****************/
// 앱은 이미 주문했는지만 본다. 서버는 내부 주문 DTO 를 그대로 내보내 금액·배송 칸을 더 싣는데 받지 않는다.
export const KmmOrderSchema = z.object({
  id: z.string(),
  status: OrderStatusSchema,
  orderDatetime: _LocalDateTimeSchema,
})
export type KmmOrder = z.infer<typeof KmmOrderSchema>

/***************** 인쇄 설정 *****************/
// 표지 PDF 를 만들 때 1mm 에 해당하는 픽셀 수. 서버가 시스템 설정 문자열로 보낸다
export const PrintOptionSchema = z.object({
  pixelMm: z.string(),
})
export type PrintOption = z.infer<typeof PrintOptionSchema>
